import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { ViewToggle } from '../../components/ViewToggle';
import {
  SettingsDetailPage, type DetailConfig, type DetailDataAdapter,
} from '../modules/SettingsDetailPage';
import type { CrudField } from '../modules/moduleCrud';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface CategoryRow {
  id:           number;
  name:         string;
  // augmented client-side after sub-category list comes back:
  subCatCount?: number;
  subCatNames?: string[];   // actual names of the linked sub-categories
}

interface SubCategoryRow { id: number; categoryId: number; categoryName: string; name: string; }

// ─────────────────────────────────────────────────────────────────────────────
// Detail config — drives the ONE shared full-page <SettingsDetailPage>
// (BO UI Standard §4, standing rule 12: list → full-page detail, never a popup).
// ─────────────────────────────────────────────────────────────────────────────
const CAT_FIELDS: CrudField[] = [
  { key: 'name', label: 'Category Name', type: 'text', required: true,
    hint: 'e.g. Surface Plant' },
  { key: 'subCats', label: 'Linked Sub Categories', type: 'readonly',
    hint: 'Sub categories must be removed before this category can be deleted.' },
];
const CAT_DETAIL_CONFIG: DetailConfig = {
  fields: CAT_FIELDS,
  formName: 'Regulatory Compliance',   // matches the server-side audit FormName
  entityLabel: 'Category',
  idField: 'id',
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function DwrcCategoriesPage() {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [rows,    setRows]    = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [search,  setSearch]  = useState('');

  // Full-page detail (BO UI Standard): null = list; { id: null } = New;
  // { id } = view-then-edit an existing record via the shared SettingsDetailPage.
  const [detail,    setDetail]    = useState<{ id: string | null } | null>(null);
  // AppDataGrid owns sort / per-column filter / pagination; this page keeps only
  // the selection set (driven by AppDataGrid's checkbox column) for Edit/Delete.
  const [selected,  setSelected]  = useState<Set<string | number>>(new Set());
  const [loadMs,    setLoadMs]    = useState<number | null>(null);
  const [saving,    setSaving]    = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      // Pull both lookups in one round-trip, then count sub-categories per cat
      const res = await api.get('/api/v1/regulatory-compliance/lookups');
      const data = (res.data as { data: { categories: CategoryRow[]; subCategories: SubCategoryRow[] } }).data;
      // Build per-category lists of sub-category names so the detail view
      // can SHOW the actual names instead of just a count.
      const namesByCat = new Map<number, string[]>();
      for (const s of data.subCategories) {
        const list = namesByCat.get(s.categoryId) ?? [];
        list.push(s.name);
        namesByCat.set(s.categoryId, list);
      }
      setRows(data.categories.map(c => ({
        ...c,
        subCatCount: namesByCat.get(c.id)?.length ?? 0,
        subCatNames: (namesByCat.get(c.id) ?? []).slice().sort((a, b) => a.localeCompare(b)),
      })));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load categories');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // ── Derived ────────────────────────────────────────────────────────────────
  // Only the global search box filters here; AppDataGrid does per-column filter,
  // sort and pagination.
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const t = search.toLowerCase();
    return rows.filter(x => x.name.toLowerCase().includes(t) || String(x.id).includes(t));
  }, [rows, search]);

  /** Ordered ids of the current list — powers the detail's prev/next arrows. */
  const siblingIds = useMemo(() => filtered.map(r => String(r.id)), [filtered]);

  // Columns for the shared grid (Category + SubCategories pill).
  const catCols: AppColumn<CategoryRow>[] = [
    { field: 'name', header: 'Category', width: 320 },
    { field: 'subCatCount', header: 'SubCategories', width: 200,
      text: r => String(r.subCatCount ?? 0),
      render: r => (
        <span className="adg-pill" style={{
          background: (r.subCatCount ?? 0) > 0 ? 'var(--ai-pill-ok-bg)' : 'var(--ai-pill-alert-bg)',
          color:      (r.subCatCount ?? 0) > 0 ? 'var(--ai-pill-ok-fg)' : 'var(--ai-pill-alert-fg)',
        }}>
          {r.subCatCount ?? 0} sub categor{(r.subCatCount ?? 0) === 1 ? 'y' : 'ies'}
        </span>
      ),
    },
  ];

  // ── Detail adapter — plugs this page's EXISTING axios calls into the shared
  //   SettingsDetailPage (same endpoints/payloads the old modal used). ─────────
  const adapter = useMemo<DetailDataAdapter>(() => ({
    load: async (id) => {
      const r = rows.find(x => String(x.id) === id);
      return {
        name:    r?.name ?? '',
        subCats: (r?.subCatNames?.length ?? 0) > 0 ? r!.subCatNames!.join(', ') : 'None',
      };
    },
    create: async (payload) => {
      const res = await api.post('/api/v1/regulatory-compliance/categories',
        { name: String(payload.name ?? '').trim() });
      const id = (res.data as { data?: { id?: number } }).data?.id;
      return id != null ? String(id) : null;
    },
    update: async (id, payload) => {
      await api.put(`/api/v1/regulatory-compliance/categories/${id}`,
        { name: String(payload.name ?? '').trim() });
    },
  }), [rows]);

  /** Edit the single selected row (toolbar button) — opens the full-page detail. */
  function openEditSelected() {
    if (selected.size === 0) { notify.warning('Select one row to edit.'); return; }
    if (selected.size > 1)   { notify.warning('Select only one row to edit.'); return; }
    setDetail({ id: String(Array.from(selected)[0]!) });
  }
  /** Delete every selected row in bulk (toolbar button). */
  async function deleteSelected() {
    if (selected.size === 0) { notify.warning('Select at least one row to delete.'); return; }
    const targets = rows.filter(r => selected.has(r.id));
    const blocked = targets.filter(r => (r.subCatCount ?? 0) > 0);
    if (blocked.length > 0) {
      notify.warning(`Cannot delete ${blocked.length} categor${blocked.length === 1 ? 'y' : 'ies'} that still have sub-categories.`);
      return;
    }
    const names = targets.map(t => t.name).join(', ');
    if (!(await confirm({
      title: targets.length === 1 ? 'Delete category?' : 'Delete categories?',
      message: `Delete ${targets.length} categor${targets.length === 1 ? 'y' : 'ies'}? (${names})`,
      tone: 'danger', confirmLabel: 'Delete',
    }))) return;
    if (saving) return;
    setSaving(true);
    try {
      await Promise.all(targets.map(t =>
        api.delete(`/api/v1/regulatory-compliance/categories/${t.id}`)));
      setSelected(new Set());
      await load();
      notify.success(`Deleted ${targets.length} categor${targets.length === 1 ? 'y' : 'ies'}.`);
    } catch (e: unknown) {
      notify.error(`Delete failed: ${apiErr(e, 'Could not delete one or more categories.')}`);
      await load();
    } finally {
      setSaving(false);
    }
  }
  function apiErr(e: unknown, fallback: string): string {
    const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
    return r?.data?.error?.message ?? (e instanceof Error ? e.message : fallback);
  }

  // ── Full-page Detail View (shared SettingsDetailPage — BO UI Standard §4) ──
  if (detail) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* List/Detail segmented switch — Detail active; List returns to the grid. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') setDetail(null); }} />
        </div>
        <SettingsDetailPage
          config={CAT_DETAIL_CONFIG}
          adapter={adapter}
          id={detail.id}
          title="Category"
          group="Reporting"
          siblingIds={siblingIds}
          onHome={() => setDetail(null)}
          onNavigate={(nid) => setDetail({ id: nid })}
          onClose={() => setDetail(null)}
          onSaved={() => { void load(); }}
        />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .rc-sel-pill {
          display:inline-flex; align-items:center; gap:6px;
          background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); color:var(--brand-accent-hover, var(--ai-orange-hover)); border:1px solid var(--ai-required-line);
          padding:3px 4px 3px 10px; border-radius:12px; font-size:11px; font-weight:600;
        }
        .rc-sel-clear {
          background:transparent; border:none; color:var(--brand-accent-hover, var(--ai-orange-hover)); cursor:pointer;
          font-size:14px; line-height:1; padding:0 6px;
        }
        .rc-sel-clear:hover { color:var(--ai-orange-dark); }
      `}</style>

      <div className="bl-page">
        <div className="bl-top">
          {/* List/Detail segmented switch (BO UI Standard) — top-LEFT.  Detail
              opens the selected row (or the first row) in the shared
              SettingsDetailPage. */}
          <div style={{ padding: '10px 0 0' }}>
            <ViewToggle view="list" detailDisabled={siblingIds.length === 0}
              onChange={(v) => {
                if (v !== 'detail') return;
                const id = selected.size === 1 ? String(Array.from(selected)[0]!) : siblingIds[0];
                if (id) setDetail({ id });
              }} />
          </div>
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Settings</span><i className="ti ti-chevron-right" />
              <span>Reporting</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Category</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6) ON the grey page,
                with the selection pill as a title-row chip. */}
            <div className="dp-title-row">
              <FormTitle pageId="dwrc-cats" loadMs={loadMs} loading={loading} />
              {selected.size > 0 && (
                <span className="rc-sel-pill" style={{ marginLeft: 10 }}>
                  <i className="ti ti-circle-check" /> {selected.size} selected
                  <button className="rc-sel-clear" onClick={() => setSelected(new Set())} title="Clear selection">×</button>
                </span>
              )}
            </div>
            <div className="dp-subtitle">
              Master list of Regulatory Compliance categories · Source: BTLIMS <code>tbl_DWRC_Category</code>
            </div>
          </div>

          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Total Categories</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : rows.length}</div>
              <div className="kpi-chg">in BTLIMS lookup</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Used by Sub Categories</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : rows.filter(r => (r.subCatCount ?? 0) > 0).length}</div>
              <div className="kpi-chg">have at least one child</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Total Sub Categories</div>
              <div className="kpi-val">{loading ? '…' : rows.reduce((a, b) => a + (b.subCatCount ?? 0), 0)}</div>
              <div className="kpi-chg">across all categories</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Orphan Categories</div>
              <div className="kpi-val kpi-red">{loading ? '…' : rows.filter(r => (r.subCatCount ?? 0) === 0).length}</div>
              <div className="kpi-chg">no sub categories</div>
            </div>
          </div>
        </div>

        <div className="bl-grid-area">
          {/* Toolbar on its OWN full-width row ON the grey page (BO UI Standard v2) —
              canonical order New · Edit · Delete · Refresh · Export to. */}
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              newLabel="Add New"
              onNew={() => setDetail({ id: null })}
              onEdit={openEditSelected}
              editDisabled={selected.size !== 1}
              onDelete={deleteSelected}
              deleteDisabled={selected.size === 0}
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: 'dwrc-categories', ...gridExportMatrix(filtered, catCols) }}
            />
          </div>

          {/* Search — shared .list-search-row on the grey page, never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" placeholder="Search categories…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
            </div>
            {(filtered.length !== rows.length) && (
              <span style={{ fontSize: 12, color: 'var(--ai-muted)' }}>{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
            )}
          </div>

          {/* ONE white grid card — grid + its single footer only. */}
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, var(--ai-orange))', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              /* Shared data grid — styling, per-column filters, sort, footer /
                 pagination, load-time and the selection checkboxes are all
                 standardised in <AppDataGrid>. */
              <AppDataGrid<CategoryRow>
                rows={filtered}
                columns={catCols}
                getRowId={r => r.id}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onRowClick={r => setDetail({ id: String(r.id) })}
                emptyText="No categories found"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
